import React, { useRef } from "react";
import { cliparts, fonts } from "../../data/designAssets";
import type { DesignElement, ElementPatch } from "../../types/configurator";
interface DesignElementViewProps {
  el: DesignElement;
  interactive: boolean;
  selected: boolean;
  onSelect?: () => void;
  onLive?: (patch: ElementPatch) => void;
  onCommit?: (patch: ElementPatch) => void;
  onGestureStart?: () => void;
  onGestureEnd?: () => void;
  onDelete?: () => void;
}
interface DragState {
  mode: "move" | "resize";
  px: number;
  py: number;
  x: number;
  y: number;
  size: number;
  width: number;
  zoneW: number;
  zoneH: number;
}
export function sizeLimits(kind: DesignElement["kind"]): [number, number] {
  return kind === "text" ? [4, 80] : [0.08, 1.2];
}
const clamp = (v: number, min: number, max: number) => Math.min(max, Math.max(min, v));
export function DesignElementView({
  el,
  interactive,
  selected,
  onSelect,
  onLive,
  onCommit,
  onGestureStart,
  onGestureEnd,
  onDelete
}: DesignElementViewProps) {
  const ref = useRef<HTMLDivElement>(null);
  const drag = useRef<DragState | null>(null);
  function start(e: React.PointerEvent, mode: DragState["mode"]) {
    if (!interactive) return;
    e.stopPropagation();
    e.preventDefault();
    onSelect?.();
    const zone = ref.current?.parentElement?.getBoundingClientRect();
    const own = ref.current?.getBoundingClientRect();
    if (!zone || !own) return;
    drag.current = {
      mode,
      px: e.clientX,
      py: e.clientY,
      x: el.x,
      y: el.y,
      size: el.size,
      width: own.width,
      zoneW: zone.width,
      zoneH: zone.height
    };
    (e.currentTarget as Element).setPointerCapture(e.pointerId);
    onGestureStart?.();
  }
  function move(e: React.PointerEvent) {
    const d = drag.current;
    if (!d) return;
    const dx = e.clientX - d.px;
    const dy = e.clientY - d.py;
    if (d.mode === "move") {
      onLive?.({
        x: clamp(d.x + dx / d.zoneW, 0, 1),
        y: clamp(d.y + dy / d.zoneH, 0, 1)
      });
    } else {
      const factor = Math.max(0.1, (d.width + dx * 2) / d.width);
      const [min, max] = sizeLimits(el.kind);
      onLive?.({
        size: clamp(d.size * factor, min, max)
      });
    }
  }
  function end() {
    if (!drag.current) return;
    drag.current = null;
    onGestureEnd?.();
  }
  function onKeyDown(e: React.KeyboardEvent) {
    const stepSize = e.shiftKey ? 0.05 : 0.01;
    const moves: Record<string, ElementPatch> = {
      ArrowLeft: {
        x: clamp(el.x - stepSize, 0, 1)
      },
      ArrowRight: {
        x: clamp(el.x + stepSize, 0, 1)
      },
      ArrowUp: {
        y: clamp(el.y - stepSize, 0, 1)
      },
      ArrowDown: {
        y: clamp(el.y + stepSize, 0, 1)
      }
    };
    if (moves[e.key]) {
      e.preventDefault();
      onCommit?.(moves[e.key]);
    } else if (e.key === "Delete" || e.key === "Backspace") {
      e.preventDefault();
      onDelete?.();
    }
  }
  return <div ref={ref} role={interactive ? "button" : undefined} tabIndex={interactive ? 0 : -1} aria-label={interactive ? describe(el) : undefined} aria-pressed={interactive ? selected : undefined} onPointerDown={e => start(e, "move")} onPointerMove={move} onPointerUp={end} onPointerCancel={end} onFocus={interactive ? onSelect : undefined} onKeyDown={interactive ? onKeyDown : undefined} className={`absolute select-none ${interactive ? "cursor-move touch-none outline-none" : "pointer-events-none"} ${selected ? "outline-dashed outline-1 outline-offset-4 outline-ink" : interactive ? "hover:outline-dashed hover:outline-1 hover:outline-offset-4 hover:outline-ink/40" : ""}`} style={{
    left: `${el.x * 100}%`,
    top: `${el.y * 100}%`,
    transform: `translate(-50%, -50%) rotate(${el.rotation}deg)`
  }} data-id="element-29" data-mp="kued1mvy8vnx">
      <ElementContent el={el} data-id="element-30" data-mp="bomrd5b630qs" />
      {selected && interactive && <span aria-hidden="true" onPointerDown={e => start(e, "resize")} className="absolute -bottom-[10px] -right-[10px] h-3 w-3 cursor-nwse-resize border border-ink bg-paper" data-id="element-31" data-mp="3txn8krpan41l" />}
    </div>;
}
function ElementContent({
  el
}: {
  el: DesignElement;
}) {
  if (el.kind === "image") {
    return <img src={el.src} alt="" draggable={false} className="block max-w-none" style={{
      width: `${el.size * 100}cqw`
    }} data-id="element-32" data-mp="1147x021adrqu" />;
  }
  if (el.kind === "clipart") {
    const Icon = cliparts.find(c => c.id === el.clipartId)?.icon;
    if (!Icon) return null;
    return <Icon aria-hidden="true" strokeWidth={2} style={{
      width: `${el.size * 100}cqw`,
      height: `${el.size * 100}cqw`,
      color: el.color
    }} data-id="element-33" data-mp="mdqx4n4av9w2" />;
  }
  const font = fonts.find(f => f.id === el.fontId) ?? fonts[0];
  return <span className="block whitespace-pre text-center leading-none" style={{
    fontFamily: font.family,
    fontWeight: font.weight,
    fontSize: `${el.size}cqw`,
    color: el.color
  }} data-id="element-34" data-mp="dcjbb4iz01iv">
      {el.text || " "}
    </span>;
}
export function describe(el: DesignElement): string {
  if (el.kind === "image") return `Logo ${el.name}`;
  if (el.kind === "clipart") return `Pictogramme ${cliparts.find(c => c.id === el.clipartId)?.name ?? ""}`;
  return `Texte « ${el.text} »`;
}